import React, { useState, useEffect } from "react";
import { C, IMGS, SERVICES, STATS, PROCESS, PORTFOLIO } from "../constants";
import { Reveal, Eyebrow, H, P, Btn, Chip, Sec } from "../components/UI";
import { useInView, useCountUp } from "../hooks";

/* ── ANIMATED STAT COUNTER ──────────────────────────────────────────────── */
function StatBlock({ stat }) {
  const [ref, inView] = useInView(0.4);
  const count = useCountUp(stat.value, inView);
  return (
    <div ref={ref} style={{ textAlign: "center", padding: "48px 16px" }}>
      <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: "clamp(40px,5vw,68px)", color: C.black, lineHeight: 1, letterSpacing: -2 }}>
        {count}
      </div>
      <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, color: C.muted, marginTop: 10 }}>
        {stat.label}
      </div>
    </div>
  );
}

/* ── MARQUEE TICKER ─────────────────────────────────────────────────────── */
function MarqueeBand() {
  const items = ["Video Production", "Ad Commercials", "Brand Films", "Social Media", "Content Strategy", "Reels", "Corporate Films", "Storytelling", "Chennai", "India"];
  return (
    <div style={{ overflow: "hidden", background: C.accent, padding: "15px 0" }}>
      <div style={{ display: "flex", animation: "marqueeTicker 28s linear infinite", whiteSpace: "nowrap" }}>
        {[...items, ...items, ...items].map((item, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 20, padding: "0 28px" }}>
            <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 12, color: C.white, letterSpacing: 3, textTransform: "uppercase" }}>{item}</span>
            <span style={{ width: 4, height: 4, background: "rgba(255,255,255,0.45)", borderRadius: "50%", flexShrink: 0 }} />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── SERVICE CARD ───────────────────────────────────────────────────────── */
function ServiceCard({ svc, i, setPage }) {
  const [hov, setHov] = useState(false);
  return (
    <Reveal delay={i * 110}>
      <div
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        onClick={() => { setPage("services"); window.scrollTo(0, 0); }}
        style={{ background: hov ? C.accent : C.white, border: `1px solid ${hov ? C.accent : C.border}`, padding: "42px 32px", transition: "all 0.32s ease", cursor: "pointer", height: "100%", display: "flex", flexDirection: "column" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 34, color: hov ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.07)", letterSpacing: -1, lineHeight: 1 }}>{svc.no}</span>
          <span style={{ fontSize: 18, color: hov ? C.white : C.accent, transform: hov ? "rotate(45deg)" : "none", transition: "transform 0.3s", display: "inline-block" }}>+</span>
        </div>
        <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "clamp(18px,2.2vw,26px)", color: hov ? C.white : C.black, lineHeight: 1.2, marginBottom: 14 }}>{svc.title}</h3>
        <P size="sm" color={hov ? "rgba(255,255,255,0.78)" : C.gray} style={{ flex: 1, marginBottom: 24 }}>{svc.tagline}</P>
        <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: hov ? "rgba(255,255,255,0.65)" : C.muted }}>Learn More →</span>
      </div>
    </Reveal>
  );
}

/* ── WORK CARD ──────────────────────────────────────────────────────────── */
function WorkCard({ item, i }) {
  const [hov, setHov] = useState(false);
  return (
    <Reveal delay={i * 90}>
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ position: "relative", overflow: "hidden", background: "#e0e0e0", cursor: "pointer" }}>
        <div style={{ paddingBottom: "72%", backgroundImage: `url(${item.img})`, backgroundSize: "cover", backgroundPosition: "center", transform: hov ? "scale(1.07)" : "scale(1)", transition: "transform 0.72s cubic-bezier(.4,0,.2,1)" }} />
        <div style={{ position: "absolute", inset: 0, background: hov ? "rgba(10,10,10,0.72)" : "rgba(10,10,10,0.32)", transition: "background 0.38s ease", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "24px" }}>
          <div style={{ transform: hov ? "translateY(0)" : "translateY(6px)", opacity: hov ? 1 : 0.82, transition: "all 0.34s" }}>
            <Chip>{item.cat}</Chip>
            <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "clamp(16px,2vw,22px)", color: C.white, margin: "10px 0 5px", letterSpacing: -0.3 }}>{item.title}</h4>
            <P size="sm" color="rgba(255,255,255,0.55)">{item.client}</P>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

/* ── HOME PAGE ──────────────────────────────────────────────────────────── */
export default function HomePage({ setPage }) {
  const [loaded, setLoaded] = useState(false);
  const [wIdx, setWIdx]     = useState(0);
  const [wVis, setWVis]     = useState(true);
  const heroWords = ["Dream It.", "Script It.", "Shoot It.", "Deliver It."];

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setWVis(false);
      setTimeout(() => { setWIdx((p) => (p + 1) % heroWords.length); setWVis(true); }, 360);
    }, 2700);
    return () => clearInterval(t);
  }, [heroWords.length]);

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end", background: C.black }}>
        
        {/* Streamable Background Video */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          <div style={{ position: "relative", width: "100%", height: "100%", opacity: 0.65 }}>
            <iframe 
              src="https://streamable.com/e/6cbzga?autoplay=1&muted=1&nocontrols=1&loop=1" 
              allow="autoplay; fullscreen" 
              style={{ border: "none", width: "100%", height: "100%", position: "absolute", left: 0, top: 0, overflow: "hidden" }}
              title="Tharagai Hero Video"
            />
          </div>
          {/* Overlays for depth and readability */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right,rgba(10,10,10,0.94) 35%,rgba(10,10,10,0.32) 100%)", zIndex: 1 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(10,10,10,0.95) 0%,transparent 55%)", zIndex: 1 }} />
        </div>

        <div style={{ position: "relative", zIndex: 2, padding: "140px clamp(20px,7vw,120px) clamp(60px,9vh,108px)", maxWidth: 1400, width: "100%" }}>
          {/* Eyebrow */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28, opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)", transition: "all 0.9s ease 0.15s" }}>
            <span style={{ width: 36, height: 2, background: C.accent }} />
            <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 4, color: C.accent, textTransform: "uppercase" }}>
              Chennai's Premier Video Production House
            </span>
          </div>

          {/* Headline */}
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(28px)", transition: "all 1.1s cubic-bezier(.22,1,.36,1) 0.3s" }}>
            <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: "clamp(52px,10vw,128px)", color: C.white, lineHeight: 0.9, letterSpacing: "-0.03em" }}>
              WE MAKE
            </div>
            <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: "clamp(52px,10vw,128px)", color: C.accent, lineHeight: 0.9, letterSpacing: "-0.03em", opacity: wVis ? 1 : 0, transform: wVis ? "translateY(0)" : "translateY(10px)", transition: "opacity 0.36s, transform 0.36s" }}>
              {heroWords[wIdx]}
            </div>
          </div>

          {/* Sub + CTA */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 28, marginTop: 44, opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(16px)", transition: "all 0.9s ease 0.72s" }}>
            <P size="lg" color="rgba(255,255,255,0.55)" style={{ maxWidth: 460, lineHeight: 1.8 }}>
              Storytelling-driven. Results-focused. We build brand narratives that connect emotionally and convert commercially.
            </P>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <Btn variant="solid" onClick={() => { setPage("work"); window.scrollTo(0, 0); }}>View Our Work</Btn>
              <Btn variant="outline" onClick={() => { setPage("contact"); window.scrollTo(0, 0); }} style={{ borderColor: "rgba(255,255,255,0.35)", color: C.white }}>
                Start a Project
              </Btn>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: "absolute", bottom: 36, right: "clamp(20px,7vw,120px)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, opacity: loaded ? 0.55 : 0, transition: "opacity 1s ease 1.3s" }}>
          <div style={{ width: 1, height: 44, background: "rgba(255,255,255,0.4)", animation: "scrollLine 2s ease-in-out infinite" }} />
          <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 8, letterSpacing: 4, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", writingMode: "vertical-rl" }}>Scroll</span>
        </div>

        {/* Floating stats panel (desktop only) */}
        <div className="tp-stats-float" style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", borderLeft: "1px solid rgba(255,255,255,0.07)" }}>
          {STATS.map((s) => (
            <div key={s.label} style={{ padding: "18px 28px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "center" }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 24, color: C.white, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 5, letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <MarqueeBand />

      {/* ── SERVICES PREVIEW ── */}
      <Sec bg={C.surface}>
        <Reveal>
          <Eyebrow text="What We Do" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 52 }}>
            <H size="xl">End-to-End<br />Creative Services</H>
            <Btn variant="ghost" onClick={() => { setPage("services"); window.scrollTo(0, 0); }}>All Services</Btn>
          </div>
        </Reveal>
        <div className="tp-3col">
          {SERVICES.map((s, i) => <ServiceCard key={s.id} svc={s} i={i} setPage={setPage} />)}
        </div>
      </Sec>

      {/* ── ABOUT SPLIT ── */}
      <section className="tp-split">
        <div style={{ position: "relative", minHeight: 520, overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMGS.director})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,10,0.28)" }} />
          <div style={{ position: "absolute", bottom: 32, left: 32, background: C.accent, color: C.white, fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 13, letterSpacing: 2, padding: "11px 22px", textTransform: "uppercase" }}>
            Since 2022
          </div>
        </div>
        <div style={{ background: C.black, padding: "80px clamp(32px,6vw,80px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Reveal>
            <Eyebrow text="Who We Are" />
            <H size="lg" color={C.white} style={{ marginTop: 10, marginBottom: 22 }}>Your All-In-One<br />Production Agency</H>
            <P size="md" color="rgba(255,255,255,0.58)" style={{ marginBottom: 14 }}>
              Founded by Sreenithi in 2022 — armed with a single phone and relentless creative spirit — Tharagai Productions grew from a one-person operation into Chennai's most sought-after production house.
            </P>
            <P size="md" color="rgba(255,255,255,0.42)" style={{ marginBottom: 36 }}>
              We don't just make content that looks good. We make content that{" "}
              <em style={{ color: C.accent, fontStyle: "normal", fontWeight: 600 }}>sells</em>.
            </P>
            <Btn variant="solid" onClick={() => { setPage("about"); window.scrollTo(0, 0); }}>Our Story</Btn>
          </Reveal>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div style={{ background: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="tp-stats-band" style={{ padding: "0 clamp(20px,7vw,120px)" }}>
          {STATS.map((s, i) => (
            <div key={s.label} style={{ borderRight: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <StatBlock stat={s} />
            </div>
          ))}
        </div>
      </div>

      {/* ── WORK PREVIEW ── */}
      <Sec bg={C.surface}>
        <Reveal>
          <Eyebrow text="Selected Projects" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 52 }}>
            <H size="xl">Stories We've Told</H>
            <Btn variant="ghost" onClick={() => { setPage("work"); window.scrollTo(0, 0); }}>View All Work</Btn>
          </div>
        </Reveal>
        <div className="tp-3col">
          {PORTFOLIO.slice(0, 3).map((item, i) => <WorkCard key={item.id} item={item} i={i} />)}
        </div>
      </Sec>

      {/* ── PROCESS ── */}
      <section style={{ background: C.black, padding: "96px clamp(20px,7vw,120px)" }}>
        <Reveal>
          <Eyebrow text="How We Work" />
          <H size="xl" color={C.white} style={{ marginBottom: 64 }}>The Process</H>
        </Reveal>
        <div className="tp-process-grid">
          {PROCESS.map((p, i) => (
            <Reveal key={p.no} delay={i * 110}>
              <div style={{ padding: "40px 32px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 52, color: "rgba(255,255,255,0.05)", lineHeight: 1, marginBottom: 18, letterSpacing: -2 }}>{p.no}</div>
                <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 17, color: C.white, letterSpacing: 0.3, marginBottom: 12 }}>{p.title}</h4>
                <P size="sm" color="rgba(255,255,255,0.42)">{p.desc}</P>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section style={{ background: C.accent, padding: "90px clamp(20px,7vw,120px)", textAlign: "center" }}>
        <Reveal>
          <H size="xl" color={C.white} style={{ marginBottom: 18 }}>Ready to Tell Your<br />Brand's Story?</H>
          <P size="lg" color="rgba(255,255,255,0.72)" style={{ maxWidth: 500, margin: "0 auto 40px", lineHeight: 1.85 }}>
            Whether you're a startup finding your voice or an established brand ready to scale — let's create something extraordinary.
          </P>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn variant="solid" onClick={() => { setPage("contact"); window.scrollTo(0, 0); }} style={{ background: C.white, borderColor: C.white, color: C.accent }}>
              Start Your Project
            </Btn>
            <Btn variant="outline" onClick={() => { setPage("work"); window.scrollTo(0, 0); }} style={{ borderColor: "rgba(255,255,255,0.55)", color: C.white }}>
              See Our Work
            </Btn>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
